import React from "react";
import { Link } from "react-router-dom";
import LegalPage, { InfoCard } from "@/components/LegalPage";
import {
  Shield, Layers, Flag, Info, UserCheck, Baby, ExternalLink, MessageCircle, ShieldAlert,
} from "lucide-react";

export default function Safety() {
  const sections = [
    {
      id: "approach", title: "Our Approach", icon: Shield,
      body: (
        <p>
          Safety at MystiCast isn&apos;t a form to fill out — it&apos;s a system. Age gating on entry, identity verification for creators,
          active moderation of streams and chat, real reporting tools that reach a real human, and the willingness to ban when someone won&apos;t behave.
        </p>
      ),
    },
    {
      id: "layers", title: "Layers of Protection", icon: Layers, highlight: "cyan",
      body: (
        <>
          <InfoCard icon={Baby} title="🔞 Age Gate">
            <p>Every visitor must confirm they are 18+ (DOB + attestation) before entering the Service.</p>
          </InfoCard>
          <InfoCard icon={UserCheck} title="🪪 Identity Verification" tone="cyan">
            <p>Streamers and tip recipients must pass Stripe Identity KYC. Prevents impersonation, underage broadcasting, and payout fraud.</p>
          </InfoCard>
          <InfoCard icon={Info} title="🛡 Chat Filters" tone="volt">
            <p>Automatic banned-word detection, link throttling, rate-limits, and new-account posting restrictions.</p>
          </InfoCard>
          <InfoCard icon={ShieldAlert} title="⚡ Moderator Tools" tone="orange">
            <p>Streamers can appoint mods with timeout, ban, and message-delete powers per-channel.</p>
          </InfoCard>
          <InfoCard icon={Flag} title="🚩 Reporting" tone="pink">
            <p>One-click reporting on streams, posts, comments, DMs, and profiles. Reports go to Trust & Safety review.</p>
          </InfoCard>
        </>
      ),
    },
    {
      id: "reporting", title: "Reporting Something Harmful", icon: Flag, highlight: "pink",
      body: (
        <>
          <p>If you see content or behavior that violates our <Link to="/community">Community Guidelines</Link>:</p>
          <ul>
            <li>Use the built-in Report button on the offending content.</li>
            <li>For urgent threats, <strong>call your local emergency services first</strong>, then message us via <Link to="/contact">Contact</Link>.</li>
            <li>For legal or copyright issues, see our <Link to="/dmca">DMCA</Link> page.</li>
          </ul>
        </>
      ),
    },
    {
      id: "after-report", title: "What Happens After a Report", icon: Info,
      body: (
        <ul>
          <li>Reports are queued for review by our Trust & Safety team.</li>
          <li>Time-sensitive reports (imminent harm, CSAM, doxxing) are triaged first.</li>
          <li>Actions can include warnings, content removal, temporary suspension, permanent ban, and referral to authorities.</li>
          <li>Reporters are notified of the outcome where possible.</li>
        </ul>
      ),
    },
    {
      id: "creator-safety", title: "Creator Safety", icon: UserCheck,
      body: (
        <ul>
          <li>Block and mute lists carry across channels.</li>
          <li>Whisper & DM controls (friends-only, verified-only, off).</li>
          <li>Followers-only chat and slow-mode for hostile brigades.</li>
          <li>Do not share personal information (address, phone, banking) on stream. We will never ask for it in chat.</li>
        </ul>
      ),
    },
    {
      id: "underage", title: "Underage Users", icon: Baby, highlight: "pink",
      body: (
        <p>
          MystiCast is 18+. If you believe a user is under 18, please report immediately. We take swift action to remove underage accounts.
        </p>
      ),
    },
    {
      id: "resources", title: "External Resources", icon: ExternalLink,
      body: (
        <ul>
          <li>Crisis support: <a href="https://findahelpline.com" target="_blank" rel="noreferrer">findahelpline.com</a></li>
          <li>Report online exploitation of a child: <a href="https://report.cybertip.org" target="_blank" rel="noreferrer">National Center for Missing & Exploited Children</a></li>
        </ul>
      ),
    },
    {
      id: "contact", title: "Contact", icon: MessageCircle,
      body: (<p>To reach Trust & Safety directly, use the <Link to="/contact">contact form</Link> and select &quot;Trust & Safety Report&quot;.</p>),
    },
  ];

  return (
    <LegalPage
      icon={Shield}
      label="TRUST & SAFETY"
      title="Trust & Safety"
      intro="How we keep MystiCast safe — for creators, viewers, and the community."
      updated="February 2026"
      sections={sections}
      contact={{
        title: "Need to report something?",
        body: "Trust & Safety is a real team. Urgent reports (imminent harm, CSAM, doxxing) are triaged first.",
        cta: { label: "Contact Trust & Safety", to: "/contact" },
      }}
    />
  );
}
